import { useEffect, useState } from 'react';
import { useGetCallByIdQuery } from '../../../../../../store/excecutive/executiveApi';
import CallDetailsTranscript from './details/CallDetailsTranscript';
import moment from 'moment';
import CallDetailsAnalytics from './details/CallDetailsAnalytics';
// import CallDetailsCost from './details/CallDetailsCost';
import { useTheme } from '../../../../../../theme-context';
import deviceAxios from '../../../../../../utils/axios/device.axios';
import { DEVICE_INFO_API_URL } from '../../../../../../config';

export default function CallLogModal({
    open,
    onClose,
    callId,
}: {
    open: boolean;
    onClose: () => void;
    callId: string;
}) {
    const [activeTab, setActiveTab] = useState<
        'transcript' | 'logs' | 'call summary' // | 'call cost'
    >('call summary');
	const {theme} = useTheme();
    const { data: callDetails } = useGetCallByIdQuery(callId);

    // Recording links returned by Vapi are now short-lived signed URLs behind an
    // authenticated endpoint, so we point <audio>/downloads at our backend proxy
    // (which fetches a fresh signed URL and 302-redirects) instead of any stored
    // recordingUrl directly. This route intentionally has no app auth guard so the
    // browser can hit it as a plain URL and use native Range requests for metadata.
    const recordingUrl = callDetails?.data.otherData.recordingUrl
        ? `${DEVICE_INFO_API_URL}/calls/all-calls/${callId}/recording`
        : null;

	useEffect(() => {
		if (open) {
		  // lock body scroll
		  document.body.style.overflow = 'hidden';
		} else {
		  document.body.style.overflow = '';
		}
	
		return () => {
		  // cleanup (important)
		  document.body.style.overflow = '';
		};
	  }, [open]);

    const handleDownloadAudio = async () => {
        if (!recordingUrl) return;
        const response = await deviceAxios.get(
            `/calls/all-calls/${callId}/recording`,
            { responseType: 'blob' },
        );
        const blobUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = callDetails?.data.otherData.callId || '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    if (!open && !callId) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
			<div className="absolute inset-0 bg-black/40" />
            <div className="w-full max-w-[90%] md:max-w-5xl max-h-[90vh] bg-white dark:bg-black dark:text-white rounded-2xl shadow-xl flex flex-col z-10" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-sm md:text-xl font-semibold">Call Log Details</h2>

                    <div className="flex items-center gap-3 text-gray-600">
                        <span className="text-sm">ID: {callId}</span>
                        <div
                            onClick={onClose}
                            className="w-5 h-5 cursor-pointer">
                            <img
                                alt="close"
                                src="/assets/shared/close-icon.svg"
                            />
                        </div>
                    </div>
                </div>
                <div className="bg-red px-6 py-4 flex flex-wrap items-center">
                    <h5 className="text-[15px] font-bold text-gray-800 dark:text-gray-500 text-sm">
                        Call Details
                    </h5>
                    {callDetails?.data.otherData.recordingUrl && <div className="flex items-center gap-3 ml-auto">
                        <span className="text-gray-500 text-sm">
                            {moment(
                                callDetails?.data.otherData.callStarted,
                            ).format('MMM DD, YYYY hh:mm A')}{' '}
                            • {callDetails?.data.otherData.duration}s duration
                        </span>
                        <button
                            className="flex items-center gap-1 border rounded-lg px-3 py-1.5 bg-gray-100 text-gray-800 hover:bg-gray-400 dark:bg-gray-800 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!recordingUrl}
                            onClick={handleDownloadAudio}>
                            <img
								className='dark:contrast-0'
                                alt="download"
                                src="/assets/shared/download.svg"
                            />
                            Download Audio
                        </button>
                    </div>}
                </div>

                {recordingUrl && <div className="px-6 py-4">
                    <div className="p-4 rounded-lg border border-gray-200">
                        <audio controls className="w-full" style={{ colorScheme: theme }}>
                            <source
                                src={recordingUrl}
                                type="audio/wav"
                            />
                            Your browser does not support the audio element.
                        </audio>
                    </div>

                    <div className="flex items-center justify-between mt-4 text-sm text-gray-500"></div>
                </div>}

                <div className="px-6 flex gap-6">
                    {['call summary', 'transcript'].map(
                        (tab) => (
                            <button
                                key={tab}
                                onClick={() =>
                                    setActiveTab(tab as 'transcript')
                                }
                                className={`pb-3 text-sm font-medium capitalize ${
                                    activeTab === tab
                                        ? 'border-b-2 border-green-600 text-green-600'
                                        : 'text-gray-500'
                                }`}>
                                {tab}
                            </button>
                        ),
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6 m-5 border border-gray-200 rounded-xl">
                    {activeTab === 'transcript' && (
                        <CallDetailsTranscript
                            transcript={
                                callDetails?.data.otherData.transcript || ''
                            }
                        />
                    )}
                    {activeTab === 'call summary' && (
                        <CallDetailsAnalytics
                            summary={callDetails?.data.otherData.summary || ''}
                            endedCallReason={
                                callDetails?.data.otherData.endedReason || ''
                            }
                            status={
                                callDetails?.data.otherData.finalStatus || ''
                            }
                            fromNumber={(callDetails?.data.otherData as any)?.fromNumber}
                            guestInformation={
                                callDetails?.data.otherData.structuredData || {
                                    email: '',
                                    guest_name: '',
                                }
                            }
							metaData={
								callDetails?.data.otherData.metaData || {}
							}
                        />
                    )}
					{/* {activeTab === 'call cost' && (
						<CallDetailsCost 
							internalCost={callDetails?.data.otherData.internalCost || 0}
							llmCost={callDetails?.data.otherData.llmCost || 0}
							sttCost={callDetails?.data.otherData.sttCost || 0}
							totalCost={callDetails?.data.otherData.totalCost || 0}
						/>
					)} */}
                </div>
            </div>
        </div>
    );
}
